import React, { useState, useEffect, useMemo } from 'react';
import { User, Plus, Trash2, Car, Phone, Mail, Save, X, KeyRound, UserCheck, Calendar, Pencil, UserPlus } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import PhoneInput from '../components/ui/phone-input';
import { formatDisplay } from '../lib/phone';
import { formatUkDateTime } from '../lib/date';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const OTHER = '__other__';

const EMPTY_DRIVER = { name: '', phone: '', email: '', vehicleModel: '', vehicleReg: '', pin: '' };

const DriverManagement = () => {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [loading, setLoading] = useState(true);

  // Driver add/edit form
  const [showDriverForm, setShowDriverForm] = useState(false);
  const [formMode, setFormMode] = useState('add'); // 'add' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [driverForm, setDriverForm] = useState(EMPTY_DRIVER);
  const [manualModel, setManualModel] = useState(false);

  // Per-booking assignment mode + third-party form
  const [assignMode, setAssignMode] = useState({}); // { [bookingId]: 'registered' | 'third' }
  const [tpForm, setTpForm] = useState({}); // { [bookingId]: { name, phone, vehicleReg, vehicleModel } }

  useEffect(() => {
    fetchDrivers();
    fetchConfirmedBookings();
    fetchFleet();
  }, []);

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(`${API}/drivers`);
      setDrivers(response.data.drivers || []);
    } catch (error) {
      toast.error('Failed to fetch drivers');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfirmedBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings?status=confirmed&limit=100`);
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const fetchFleet = async () => {
    try {
      const res = await axios.get(`${API}/fleet`);
      setFleet(res.data.vehicles || []);
    } catch (error) {
      console.error('Error fetching fleet:', error);
    }
  };

  const fleetNames = useMemo(() => fleet.map((v) => v.name), [fleet]);

  const openAddDriver = () => {
    setDriverForm(EMPTY_DRIVER);
    setManualModel(false);
    setFormMode('add');
    setEditingId(null);
    setShowDriverForm(true);
  };

  const openEditDriver = (driver) => {
    const inFleet = !driver.vehicleModel || fleetNames.includes(driver.vehicleModel);
    setDriverForm({
      name: driver.name || '', phone: driver.phone || '', email: driver.email || '',
      vehicleModel: driver.vehicleModel || '', vehicleReg: driver.vehicleReg || '', pin: '',
    });
    setManualModel(!inFleet && !!driver.vehicleModel);
    setFormMode('edit');
    setEditingId(driver.id);
    setShowDriverForm(true);
  };

  const closeDriverForm = () => {
    setShowDriverForm(false);
    setEditingId(null);
    setDriverForm(EMPTY_DRIVER);
    setManualModel(false);
  };

  const handleSaveDriver = async () => {
    if (!driverForm.name || !driverForm.phone || !driverForm.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      if (formMode === 'edit') {
        await axios.put(`${API}/admin/drivers/${editingId}`, {
          name: driverForm.name, phone: driverForm.phone, email: driverForm.email,
          vehicleModel: driverForm.vehicleModel, vehicleReg: driverForm.vehicleReg,
        });
        toast.success('Driver updated');
      } else {
        const res = await axios.post(`${API}/drivers`, driverForm);
        const initialPin = res.data?.driver?.initialPin;
        toast.success(initialPin ? `Driver added · PIN: ${initialPin}` : 'Driver added successfully!');
      }
      closeDriverForm();
      fetchDrivers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save driver');
    }
  };

  const handleChangePin = async (driver) => {
    const newPin = window.prompt(`Enter a new PIN for ${driver.name} (4-6 digits):`);
    if (newPin === null) return;
    const clean = newPin.replace(/\D/g, '');
    if (clean.length < 4 || clean.length > 6) {
      toast.error('PIN must be 4-6 digits');
      return;
    }
    try {
      await axios.post(`${API}/admin/drivers/${driver.id}/set-pin`, { newPin: clean });
      toast.success(`PIN updated for ${driver.name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set PIN');
    }
  };

  const handleDeleteDriver = async (driverId) => {
    if (!window.confirm('Are you sure you want to remove this driver?')) return;
    try {
      await axios.delete(`${API}/drivers/${driverId}`);
      toast.success('Driver removed successfully');
      fetchDrivers();
    } catch (error) {
      toast.error('Failed to remove driver');
    }
  };

  const handleAssignDriver = async (bookingId, driverId) => {
    try {
      await axios.post(`${API}/bookings/${bookingId}/assign-driver`, { driverId });
      toast.success('Driver assigned — customer notified');
      fetchConfirmedBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign driver');
    }
  };

  const handleAssignThirdParty = async (bookingId) => {
    const form = tpForm[bookingId] || {};
    if (!form.name || !form.phone || !form.vehicleReg) {
      toast.error('Name, phone and vehicle registration are required');
      return;
    }
    try {
      await axios.post(`${API}/bookings/${bookingId}/assign-third-party-driver`, {
        name: form.name, phone: form.phone, vehicleReg: form.vehicleReg, vehicleModel: form.vehicleModel || '',
      });
      toast.success('Third-party driver assigned — driver texted, customer notified');
      fetchConfirmedBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign third-party driver');
    }
  };

  const setTp = (bookingId, key, value) =>
    setTpForm((prev) => ({ ...prev, [bookingId]: { ...(prev[bookingId] || {}), [key]: value } }));

  const stats = useMemo(() => {
    const total = drivers.length;
    const active = drivers.filter((d) => (d.assignedBookings || 0) > 0).length;
    const unassigned = bookings.filter((b) => !b.assignedDriverName).length;
    return { total, active, confirmed: bookings.length, unassigned };
  }, [drivers, bookings]);

  const StatCard = ({ icon: Icon, label, value, testId }) => (
    <Card className="p-4 flex items-center gap-3" data-testid={testId}>
      <div className="h-9 w-9 rounded-lg bg-black text-white flex items-center justify-center">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
        <p className="text-xl font-bold text-black leading-tight">{value}</p>
      </div>
    </Card>
  );

  return (
    <AdminLayout
      title="Driver Management"
      subtitle="Add & edit drivers, assign cars, and dispatch registered or third-party drivers"
      actions={
        !showDriverForm && (
          <Button onClick={openAddDriver} className="bg-black text-white hover:bg-gray-800" data-testid="add-driver-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Driver
          </Button>
        )
      }
    >
      <div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard icon={User} label="Drivers" value={stats.total} testId="driver-stat-total" />
          <StatCard icon={UserCheck} label="On Active Jobs" value={stats.active} testId="driver-stat-active" />
          <StatCard icon={Calendar} label="Confirmed Bookings" value={stats.confirmed} testId="driver-stat-confirmed" />
          <StatCard icon={Car} label="Awaiting Driver" value={stats.unassigned} testId="driver-stat-unassigned" />
        </div>

        {/* Add / Edit Driver Form */}
        {showDriverForm && (
          <div className="mb-8">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-black">{formMode === 'edit' ? 'Edit Driver' : 'Add New Driver'}</h2>
                <Button onClick={closeDriverForm} variant="ghost" size="sm"><X className="h-5 w-5" /></Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <Label className="text-gray-900 mb-2 font-medium">Driver Name *</Label>
                  <Input value={driverForm.name} onChange={(e) => setDriverForm({ ...driverForm, name: e.target.value })} placeholder="John Smith" className="bg-white border-gray-300" data-testid="driver-name-input" />
                </div>
                <div>
                  <Label className="text-gray-900 mb-2 font-medium">Phone Number *</Label>
                  <PhoneInput value={driverForm.phone} onChange={(val) => setDriverForm({ ...driverForm, phone: val })} className="bg-white border-gray-300" />
                </div>
                <div>
                  <Label className="text-gray-900 mb-2 font-medium">Email Address *</Label>
                  <Input type="email" value={driverForm.email} onChange={(e) => setDriverForm({ ...driverForm, email: e.target.value })} placeholder="driver@example.com" className="bg-white border-gray-300" data-testid="driver-email-input" />
                </div>
                <div>
                  <Label className="text-gray-900 mb-2 font-medium">Assigned Car</Label>
                  <Select
                    value={manualModel ? OTHER : (driverForm.vehicleModel || '')}
                    onValueChange={(val) => {
                      if (val === OTHER) { setManualModel(true); setDriverForm({ ...driverForm, vehicleModel: '' }); }
                      else { setManualModel(false); setDriverForm({ ...driverForm, vehicleModel: val }); }
                    }}
                  >
                    <SelectTrigger className="bg-white border-gray-300" data-testid="driver-car-select"><SelectValue placeholder="Select a car from your fleet..." /></SelectTrigger>
                    <SelectContent>
                      {fleet.map((v) => (
                        <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>
                      ))}
                      <SelectItem value={OTHER}>Other (type manually)…</SelectItem>
                    </SelectContent>
                  </Select>
                  {manualModel && (
                    <Input value={driverForm.vehicleModel} onChange={(e) => setDriverForm({ ...driverForm, vehicleModel: e.target.value })} placeholder="Vehicle model" className="bg-white border-gray-300 mt-2" data-testid="driver-car-manual-input" />
                  )}
                </div>
                <div>
                  <Label className="text-gray-900 mb-2 font-medium">Vehicle Registration</Label>
                  <Input value={driverForm.vehicleReg} onChange={(e) => setDriverForm({ ...driverForm, vehicleReg: e.target.value })} placeholder="XX00 XXX" className="bg-white border-gray-300" data-testid="driver-reg-input" />
                </div>
                {formMode === 'add' && (
                  <div>
                    <Label className="text-gray-900 mb-2 font-medium">Sign-in PIN (4–6 digits)</Label>
                    <Input value={driverForm.pin} onChange={(e) => setDriverForm({ ...driverForm, pin: e.target.value.replace(/\D/g, '').slice(0, 6) })} placeholder="Defaults to last 4 digits of phone" className="bg-white border-gray-300" inputMode="numeric" data-testid="driver-pin-input" />
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <Button onClick={closeDriverForm} variant="outline" className="flex-1 border-gray-300">Cancel</Button>
                <Button onClick={handleSaveDriver} className="flex-1 bg-black text-white hover:bg-gray-800" data-testid="driver-save-btn">
                  <Save className="h-4 w-4 mr-2" /> {formMode === 'edit' ? 'Save Changes' : 'Add Driver'}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Drivers List */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-black">All Drivers ({drivers.length})</h2>
          {loading ? (
            <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div></div>
          ) : drivers.length === 0 ? (
            <Card className="p-12 text-center">
              <User className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">No drivers added yet</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {drivers.map((driver) => (
                <Card key={driver.id} className="p-6 hover:shadow-lg transition-all relative overflow-hidden" data-testid={`driver-card-${driver.id}`}>
                  <div className="absolute top-0 left-0 h-1 w-full" style={{ backgroundColor: driver.color || '#475569' }} />
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate(`/admin/drivers/${encodeURIComponent(driver.id)}`)}>
                      <div className="w-12 h-12 rounded-full text-white flex items-center justify-center font-bold text-lg shadow-sm" style={{ backgroundColor: driver.color || '#000' }}>
                        {driver.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-black">{driver.name}</h3>
                        <p className="text-sm text-gray-500 flex items-center gap-1.5">
                          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: driver.color || '#000' }} />
                          Map key colour
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Button onClick={(e) => { e.stopPropagation(); openEditDriver(driver); }} variant="ghost" size="sm" className="text-gray-600 hover:text-black hover:bg-gray-100" data-testid={`driver-edit-${driver.id}`} title="Edit driver">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button onClick={(e) => { e.stopPropagation(); handleChangePin(driver); }} variant="ghost" size="sm" className="text-gray-600 hover:text-black hover:bg-gray-100" data-testid={`driver-change-pin-${driver.id}`} title="Change sign-in PIN">
                        <KeyRound className="h-4 w-4" />
                      </Button>
                      <Button onClick={(e) => { e.stopPropagation(); handleDeleteDriver(driver.id); }} variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`driver-delete-${driver.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="h-4 w-4" />
                      <a href={`tel:${driver.phone}`} className="hover:text-black">{formatDisplay(driver.phone)}</a>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="h-4 w-4" />
                      <a href={`mailto:${driver.email}`} className="hover:text-black truncate">{driver.email}</a>
                    </div>
                    {driver.vehicleModel && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Car className="h-4 w-4" />
                        <span>{driver.vehicleModel} {driver.vehicleReg && `(${driver.vehicleReg})`}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500">Assigned: <span className="font-semibold text-black">{driver.assignedBookings || 0}</span> active bookings</p>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Assign Drivers to Bookings */}
        <div>
          <h2 className="text-2xl font-bold mb-6 text-black">Assign Drivers to Bookings</h2>
          {bookings.length === 0 ? (
            <Card className="p-12 text-center"><p className="text-gray-600">No confirmed bookings available for assignment</p></Card>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => {
                const mode = assignMode[booking.id] || 'registered';
                return (
                  <Card key={booking.id} className="p-6">
                    <div className="flex flex-col lg:flex-row gap-6 items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                          <span className="font-mono font-bold text-black">{booking.id}</span>
                          {booking.assignedDriverName && (
                            <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                              {booking.isThirdPartyDriver ? '3rd-Party: ' : 'Driver: '}{booking.assignedDriverName}
                              {booking.driverVehicleReg ? ` · ${booking.driverVehicleReg}` : ''}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div><p className="text-gray-600">Customer</p><p className="font-semibold text-black">{booking.firstName} {booking.lastName}</p></div>
                          <div><p className="text-gray-600">Date & Time</p><p className="font-semibold text-black">{formatUkDateTime(booking.pickupDate, booking.pickupTime)}</p></div>
                          <div><p className="text-gray-600">Pickup</p><p className="font-semibold text-black text-sm">{booking.pickupLocation}</p></div>
                          <div><p className="text-gray-600">Drop-off</p><p className="font-semibold text-black text-sm">{booking.dropoffLocation}</p></div>
                        </div>
                      </div>

                      <div className="w-full lg:w-80">
                        {/* Mode toggle */}
                        <div className="flex gap-2 mb-3">
                          <button
                            onClick={() => setAssignMode({ ...assignMode, [booking.id]: 'registered' })}
                            className={`flex-1 text-xs font-semibold py-2 rounded border ${mode === 'registered' ? 'bg-black text-white border-black' : 'bg-white text-gray-700 border-gray-300'}`}
                            data-testid={`assign-mode-registered-${booking.id}`}
                          >
                            <Car className="h-3.5 w-3.5 inline mr-1" /> Registered
                          </button>
                          <button
                            onClick={() => setAssignMode({ ...assignMode, [booking.id]: 'third' })}
                            className={`flex-1 text-xs font-semibold py-2 rounded border ${mode === 'third' ? 'bg-black text-white border-black' : 'bg-white text-gray-700 border-gray-300'}`}
                            data-testid={`assign-mode-third-${booking.id}`}
                          >
                            <UserPlus className="h-3.5 w-3.5 inline mr-1" /> Third-Party
                          </button>
                        </div>

                        {mode === 'registered' ? (
                          <Select
                            value={booking.assignedDriverId || ''}
                            onValueChange={(value) => handleAssignDriver(booking.id, value)}
                          >
                            <SelectTrigger className="bg-white border-gray-300" data-testid={`assign-driver-select-${booking.id}`}><SelectValue placeholder="Select driver..." /></SelectTrigger>
                            <SelectContent>
                              {drivers.map((driver) => (
                                <SelectItem key={driver.id} value={driver.id}>
                                  {driver.name}{driver.vehicleModel ? ` · ${driver.vehicleModel}` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="space-y-2 bg-gray-50 border border-gray-200 rounded p-3">
                            <Input placeholder="Driver name *" value={tpForm[booking.id]?.name || ''} onChange={(e) => setTp(booking.id, 'name', e.target.value)} className="bg-white border-gray-300 h-9 text-sm" data-testid={`tp-name-${booking.id}`} />
                            <PhoneInput value={tpForm[booking.id]?.phone || ''} onChange={(val) => setTp(booking.id, 'phone', val)} className="bg-white border-gray-300" />
                            <Input placeholder="Vehicle registration *" value={tpForm[booking.id]?.vehicleReg || ''} onChange={(e) => setTp(booking.id, 'vehicleReg', e.target.value)} className="bg-white border-gray-300 h-9 text-sm" data-testid={`tp-reg-${booking.id}`} />
                            <Input placeholder="Vehicle model (optional)" value={tpForm[booking.id]?.vehicleModel || ''} onChange={(e) => setTp(booking.id, 'vehicleModel', e.target.value)} className="bg-white border-gray-300 h-9 text-sm" data-testid={`tp-model-${booking.id}`} />
                            <Button onClick={() => handleAssignThirdParty(booking.id)} className="w-full bg-black text-white hover:bg-gray-800 h-9 text-sm" data-testid={`tp-assign-${booking.id}`}>
                              Assign Third-Party Driver
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default DriverManagement;
